import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  COOKIE_EXPIRATION,
  COOKIE_NAMES,
  DEFAULT_COOKIE_OPTIONS,
} from "@constants/cookies";
import { changeLanguage } from "i18next";

import { safeLocalStorage } from "@lib/safeStorage";

export const I18N_LOCALE_LNG_KEY = "i18nextLng";

/**
 * The locale the app settles on at boot, from the three stores in priority
 * order: `?lng=` (the join page links with it), the `loc` cookie (the old
 * React app), localStorage (this app). Pure so the pre-mount member
 * prefetch (lib/memberPrefetch.ts) can key the experiments query exactly
 * like LocaleProvider's mount effect below does; the two must not drift.
 */
export const resolveInitialLocale = ({
  queryLng,
  cookieLng,
  storedLng,
}: {
  queryLng: unknown;
  cookieLng: unknown;
  storedLng: unknown;
}): string => {
  const finalLocale =
    pickLocaleCandidate(queryLng) ||
    pickLocaleCandidate(cookieLng) ||
    pickLocaleCandidate(storedLng) ||
    "en";
  return finalLocale === "en-US" ? "en" : finalLocale;
};

/**
 * A locale read from the query string, the `loc` cookie or localStorage, or
 * `undefined` when there is nothing usable there. Links built with an
 * undefined locale variable (`/gifts?lng=undefined`) used to be taken
 * verbatim and PERSISTED into the cookie and localStorage, so every later
 * visit fed "undefined" to Intl and crashed the gift date picker (Sentry
 * NMH-REACT-D9N, OMG-2592). Treat the stringified nothings as absent; the
 * stores are then rewritten with the resolved value below, which heals a
 * browser that already carries the bad value.
 */
export const pickLocaleCandidate = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (trimmed === "" || trimmed === "undefined" || trimmed === "null") {
    return undefined;
  }
  return trimmed;
};

interface LocaleContextType {
  // TODO: it sould be: locale: LanguageCode;
  locale: string;
  changeLocale: (locale: string) => void;
  /**
   * The locale the mount effect below RESOLVED from the three stores (query
   * string, `loc` cookie, localStorage), or null before that effect has run.
   * `locale` above is i18next's `resolvedLanguage`, which (a) never reads the
   * `loc` cookie and (b) only flips after `changeLanguage` has fetched the
   * new namespace, so for a member whose stores disagree with i18next's first
   * guess it is wrong for a full network round trip. Anything that PERSISTS
   * a decision keyed on the locale (the Collections Home A/B eligibility
   * evaluation, OMG-2581) must read THIS value; anything that merely renders
   * copy keeps reading `locale`.
   */
  settledLocale: string | null;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

export const LocaleProvider = ({ children }: { children: ReactNode }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [cookies, setCookie] = useCookies<string>([COOKIE_NAMES.LOCALE]);
  const {
    i18n: { resolvedLanguage: locale },
  } = useTranslation();
  const [settledLocale, setSettledLocale] = useState<string | null>(null);

  useEffect(() => {
    // Join page uses the lng query parameter to set the locale
    const queryLng = pickLocaleCandidate(searchParams.get("lng"));

    // New React app use localStorage to store the locale
    const storedLng = pickLocaleCandidate(
      safeLocalStorage.getItem(I18N_LOCALE_LNG_KEY),
    );

    // Old React app use cookies to store the locale
    const cookieLng = pickLocaleCandidate(cookies[COOKIE_NAMES.LOCALE]);

    const finalLocale = resolveInitialLocale({
      queryLng,
      cookieLng,
      storedLng,
    });

    // If the locale is not the same as the cookie, set the cookie
    if (finalLocale !== cookieLng) {
      setCookie(COOKIE_NAMES.LOCALE, finalLocale, {
        ...DEFAULT_COOKIE_OPTIONS,
        expires: COOKIE_EXPIRATION.NEVER,
      });
    }

    // If the locale is not the same as the localStorage, set the localStorage
    if (finalLocale !== storedLng)
      safeLocalStorage.setItem(I18N_LOCALE_LNG_KEY, finalLocale);

    changeLanguage(finalLocale);
    // Only rewrite the URL when there is an lng to strip. The rewrite is a
    // navigation resolved against the path this provider rendered with, and
    // passive effects run child first, so it lands AFTER any redirect a page
    // fired on the same first commit and undoes it. Done unconditionally, it
    // sent the catch-all's /home redirect straight back to the unknown path:
    // a blank page (OMG-2676).
    if (searchParams.has("lng")) {
      searchParams.delete("lng");
      setSearchParams(searchParams, { replace: true });
    }
    // Synchronous on purpose: this is the reconciled value, not i18next's
    // (asynchronous) acknowledgement of it.
    setSettledLocale(finalLocale);
  }, []);

  const changeLocale = (locale: string) => {
    setSettledLocale(locale);
    safeLocalStorage.setItem(I18N_LOCALE_LNG_KEY, locale);
    setCookie(COOKIE_NAMES.LOCALE, locale, {
      ...DEFAULT_COOKIE_OPTIONS,
      expires: COOKIE_EXPIRATION.NEVER,
    });
    changeLanguage(locale);
  };

  return (
    <LocaleContext.Provider
      value={{ locale: locale || "en", changeLocale, settledLocale }}
    >
      {children}
    </LocaleContext.Provider>
  );
};

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (context === undefined) {
    throw new Error("useLocale must be used within a LocaleProvider");
  }
  return context;
};
